import type {
  MaterializedMenuProduct,
  QualifyingStatusProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import {
  useGetSpecialQualifyingStatus,
  useRemoveCartProduct as useV3RemoveCartProduct,
  useUpdateCartProduct as useV3UpdateCartProduct,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  allowDiscountsOnRequiredProducts,
  buildFilter,
  getDiscountLabel,
  getDiscountedWithoutSameQualified,
  getPriceIdsForStoreSpecial,
} from '@iheartjane/menu';
import type { PriceId } from '@iheartjane/types';
import { useEffect } from 'react';

import {
  FilterAndSort,
  MenuProducts,
  SpecialDetail,
  getColumnCount,
} from '@jane/kiosk/components';
import {
  useRemoveCartProduct,
  useUpdateCartProduct,
} from '@jane/kiosk/data-access';
import { useKioskCart } from '@jane/kiosk/hooks';
import {
  useMenuProductSearch,
  useProductTracking,
} from '@jane/kiosk/providers';
import { getStoreSpecialStatusText } from '@jane/kiosk/util';
import { AVAILABLE_WEIGHTS } from '@jane/search/util';
/* eslint-disable-next-line no-restricted-imports */
import type { StoreSpecial as tStoreSpecial } from '@jane/shared/models';
import type { Store, StoreSpecial } from '@jane/shared/types';

// A special that allows every weight has no weight restrictions
const restrictedPriceIds = (priceIds: PriceId[]) =>
  priceIds.length > 0 && priceIds.length !== AVAILABLE_WEIGHTS.length
    ? priceIds
    : undefined;

interface SpecialInnerProps {
  navigateToCart: () => void;
  navigateToMenu: () => void;
  store: Store;
  storeSpecial: StoreSpecial | tStoreSpecial | V3StoreSpecial;
}

export const SpecialInner = ({
  navigateToCart,
  navigateToMenu,
  store,
  storeSpecial,
}: SpecialInnerProps) => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const multipleConditions = useFlag(FLAGS.c3MultipleConditionSpecials);
  const { data: cart } = useKioskCart();
  const { data: qualifyingStatusData, refetch: fetchStatus } =
    useGetSpecialQualifyingStatus({
      cartUuid: cart?.uuid,
      specialId: storeSpecial?.id,
      storeId: store?.id,
    });
  const qualifyingStatus = qualifyingStatusData?.qualifying_status;

  const { mutate: removeCartProduct } = useRemoveCartProduct({});
  const { mutate: updateProduct } = useUpdateCartProduct({});
  const { mutate: v3RemoveCartProduct } = useV3RemoveCartProduct({});
  const { mutate: v3UpdateProduct } = useV3UpdateCartProduct({});
  const {
    cartToppers: { removeCartTopperId },
    inlineAds: { removeInlineAdId },
  } = useProductTracking();

  const handleRemoveProduct = (product: QualifyingStatusProduct) => {
    const cartProduct = cart?.products.find(
      ({ id, price_id }) => id === product.id && price_id === product.price_id
    );
    const cartUuid = cart?.uuid;

    if (!cartProduct || !cartUuid) return;

    if (cartProduct.count === 1) {
      if (useV3Cart) {
        v3RemoveCartProduct(
          {
            appMode: 'kiosk',
            cartUuid,
            productId: cartProduct.id,
            priceId: cartProduct.price_id,
          },
          {
            onSuccess: () => {
              removeInlineAdId(cartProduct.id);
              removeCartTopperId(cartProduct.id);
            },
          }
        );
      } else {
        removeCartProduct(
          {
            cart_uuid: cartUuid,
            product_id: cartProduct.id,
          },
          {
            onSuccess: () => {
              removeInlineAdId(cartProduct.id);
              removeCartTopperId(cartProduct.id);
            },
          }
        );
      }
    } else if (useV3Cart) {
      v3UpdateProduct({
        appMode: 'kiosk',
        cartUuid,
        productId: cartProduct.product_id,
        count: cartProduct.count - 1,
        priceId: cartProduct.price_id,
      });
    } else {
      updateProduct({
        cart_uuid: cartUuid,
        product: {
          count: cartProduct.count - 1,
          price_id: cartProduct.price_id,
          product_id: cartProduct.product_id,
        },
      });
    }
  };

  const { calloutText, reachedMaxDiscountables, remainingQualifiers } =
    getStoreSpecialStatusText({
      currentSpecial: storeSpecial as StoreSpecial,
      qualifyingStatus: qualifyingStatusData,
    });

  const discountedWithoutSameQualified =
    getDiscountedWithoutSameQualified(qualifyingStatusData);

  const allowQualifiersToBeDiscounted = allowDiscountsOnRequiredProducts(
    storeSpecial as StoreSpecial
  );
  const discountedProductsToShow = allowQualifiersToBeDiscounted
    ? discountedWithoutSameQualified
    : qualifyingStatusData?.discounted_products;

  const isSpendingThreshold =
    storeSpecial.special_type === 'spending_threshold';
  const showBundleProgress =
    storeSpecial?.special_type === 'bundle' || isSpendingThreshold;

  const applicablePriceIds = (product?: MaterializedMenuProduct) =>
    getPriceIdsForStoreSpecial({
      multipleConditions,
      product,
      qualifyingStatus: qualifyingStatus ?? 'not_qualified',
      special: storeSpecial as StoreSpecial,
    });

  const visiblePriceIds = restrictedPriceIds(applicablePriceIds());

  // if only some weights are returned, we want to filter by weight
  const availableWeightsFilter = visiblePriceIds
    ? buildFilter(
        'available_weights',
        visiblePriceIds.map((priceId) => priceId.split('_').join(' '))
      )
    : '';

  const defaultSortName = store?.optional_filters ? 'Featured' : null;

  const productsQuery = useMenuProductSearch({
    category: 'all',
    columnCount: getColumnCount(),
    defaultSortName,
    featuredCategory: availableWeightsFilter,
    isEnabled: !!storeSpecial,
    qualifyingStatus: qualifyingStatusData?.qualifying_status,
    store,
    storeSpecial: storeSpecial,
  });

  // sets the correct top value so the sticky headers group together
  const pageHeaderHeight =
    // eslint-disable-next-line @iheartjane/workspace/no-dom-globals-in-react-fc
    window.document.getElementById('store-special-page-header')?.clientHeight ||
    0;

  useEffect(() => {
    fetchStatus();
  }, [cart?.uuid, cart?.products, fetchStatus]);

  return (
    <SpecialDetail>
      <SpecialDetail.Layout top={pageHeaderHeight}>
        <SpecialDetail.Header
          specialDescription={storeSpecial.description ?? ''}
          specialTitle={storeSpecial.title}
          specialTerms={storeSpecial.terms ?? undefined}
        >
          {showBundleProgress && (
            <SpecialDetail.Progress
              allowQualifiersToBeDiscounted={allowQualifiersToBeDiscounted}
              discountedProducts={
                (discountedProductsToShow as QualifyingStatusProduct[]) || []
              }
              onRemoveProduct={(product) => handleRemoveProduct(product)}
              qualifyBySpendAmount={isSpendingThreshold}
              qualifiedProducts={
                (qualifyingStatusData?.qualified_products as QualifyingStatusProduct[]) ||
                []
              }
              qualifiedProductsTotal={qualifyingStatusData?.product_price}
              qualifyingStatus={qualifyingStatus || 'not_qualified'}
              qualifyingTargetAmount={
                qualifyingStatusData?.qualifying_threshold
              }
              reachedMaxDiscountables={reachedMaxDiscountables}
              remainingQualifiers={
                isSpendingThreshold
                  ? !!qualifyingStatusData?.qualified_products &&
                    qualifyingStatusData.qualified_products.length > 0
                    ? 0
                    : 1
                  : remainingQualifiers
              }
              tagLabel={getDiscountLabel({
                discountAmount: storeSpecial.discount_amount,
                discountType: storeSpecial.discount_type,
                specialType: storeSpecial.special_type,
                storeComplianceLanguageSettings:
                  store?.store_compliance_language_settings,
              })}
            />
          )}
        </SpecialDetail.Header>

        {showBundleProgress && (
          <>
            <SpecialDetail.Callout calloutText={calloutText} />

            <SpecialDetail.Footer
              onCartClick={navigateToCart}
              onShopMoreClick={navigateToMenu}
              qualifyingStatus={qualifyingStatus || 'not_qualified'}
            />
          </>
        )}
      </SpecialDetail.Layout>

      <FilterAndSort
        condensedView
        defaultSortName={defaultSortName}
        optionalFilters={store.optional_filters}
        searchProps={productsQuery}
      />
      <MenuProducts
        category="specials"
        searchProps={productsQuery}
        showProductCount={false}
        visiblePriceIds={(product) =>
          restrictedPriceIds(applicablePriceIds(product))
        }
      />
    </SpecialDetail>
  );
};
